import React, { useMemo, useCallback, useLayoutEffect, useRef } from 'react';
import { Column } from 'react-data-grid';
import DataGrid from 'react-data-grid';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  COLUMN_KEYS,
  COLUMN_HEADERS,
  COLUMN_WIDTHS,
  ValidatedGDORow,
  ColumnKey,
} from './GDOBulkUploadTypes';

interface GDOValidationPreviewTableProps {
  rows: ValidatedGDORow[];
  showValidOnly: boolean;
  onShowValidOnlyChange: (val: boolean) => void;
  selectedRowIds: Set<number>;
  onRowSelectionChange: (rowId: number, selected: boolean) => void;
  onSelectAllValid: (selected: boolean) => void;
}

const STATUS_COLUMN_WIDTH = 60;
const SELECT_COLUMN_WIDTH = 60;

const ERROR_ROW_BG = '#FFE0E0';
const ERROR_CELL_BG = '#FF9999';

export function GDOValidationPreviewTable({
  rows,
  showValidOnly,
  onShowValidOnlyChange,
  selectedRowIds,
  onRowSelectionChange,
  onSelectAllValid,
}: GDOValidationPreviewTableProps) {
  const displayRows = useMemo(() => {
    const filtered = showValidOnly ? rows.filter((r) => r.isValid) : rows;
    return [...filtered].sort((a, b) => a.gdoName.localeCompare(b.gdoName));
  }, [rows, showValidOnly]);

  const columns = useMemo(() => buildColumns(displayRows, selectedRowIds, onRowSelectionChange, onSelectAllValid), [displayRows, selectedRowIds, onRowSelectionChange, onSelectAllValid]);

  const totalRows = rows.length;
  const validRows = rows.filter((r) => r.isValid).length;
  const errorRows = totalRows - validRows;

  const allValidIds = useMemo(() => rows.filter((r) => r.isValid).map((r) => r.id), [rows]);
  const allValidSelected = allValidIds.length > 0 && allValidIds.every((id) => selectedRowIds.has(id));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 400 }}>
      <Box sx={{ mb: 1.5, p: 1.5, bg: '#F5F8FC', borderRadius: 1, border: '1px solid #E0E6ED' }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#0F3764' }}>
          {totalRows > 0
            ? `${totalRows} rows parsed | ${validRows} rows valid | ${errorRows} rows with errors`
            : 'No data rows found. Please add at least one GDO row and re-upload.'}
        </Typography>
      </Box>

      {/* <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={showValidOnly}
              onChange={(e) => onShowValidOnlyChange(e.target.checked)}
              size="small"
            />
          }
          label="Show valid records only"
          labelPlacement="start"
        />
      </Box> */}

      <Box
        sx={{
          flex: 1,
          border: '1px solid #E0E6ED',
          borderRadius: 1,
          overflow: 'scroll',
          position: 'relative',
          '& .rdg': {
            background: 'transparent',
            border: 'none',
            overflowY: 'visible',
          },
          '& .rdg-header-row': {
            background: '#003771 !important',
          },
          '& .rdg-header-row .rdg-cell': {
            background: '#003771 !important',
            color: '#FFFFFF !important',
            fontWeight: 600,
            fontSize: '13px',
            whiteSpace: 'nowrap',
          },
          '& .rdg-cell-frozen[role="columnheader"]': {
            backgroundColor: '#003771 !important',
            color: '#FFFFFF !important',
          },
        }}
      >
        <DataGrid
          rows={displayRows}
          columns={columns}
          rowHeight={44}
          headerRowHeight={48}
          rowKeyGetter={(row) => row.id}
        />
      </Box>
    </Box>
  );
}

function buildColumns(displayRows: ValidatedGDORow[], selectedRowIds: Set<number>, onRowSelectionChange: (rowId: number, selected: boolean) => void, onSelectAllValid: (selected: boolean) => void): Column<ValidatedGDORow>[] {
  return [
    {
      key: 'status',
      name: '',
      width: STATUS_COLUMN_WIDTH,
      frozen: true,
      resizable: false,
      sortable: false,
      formatter: StatusFormatter,
    },
    {
      key: 'select',
      name: '',
      width: SELECT_COLUMN_WIDTH,
      frozen: true,
      resizable: false,
      sortable: false,
      formatter: createSelectFormatter(selectedRowIds, onRowSelectionChange),
      headerRenderer: createSelectHeaderRenderer(displayRows, selectedRowIds, onSelectAllValid),
    },
    ...COLUMN_KEYS.map((key) => ({
      key,
      name: COLUMN_HEADERS[key],
      width: COLUMN_WIDTHS[key],
      frozen: key === 'gdoName',
      minWidth: 100,
      maxWidth: 400,
      resizable: true,
      sortable: false,
      formatter: createDataCellFormatter(key),
    })),
  ];
}

function StatusFormatter({ row }: { row: ValidatedGDORow }) {
  if (row.isValid) {
    return (
      <Tooltip title="Valid row" placement="top">
        <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 20, cursor: 'default' }} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Row has validation errors" placement="top">
      <WarningIcon sx={{ color: '#D32F2F', fontSize: 20, cursor: 'help' }} />
    </Tooltip>
  );
}

function createSelectFormatter(selectedRowIds: Set<number>, onRowSelectionChange: (rowId: number, selected: boolean) => void) {
  return ({ row }: { row: ValidatedGDORow }) => {
    if (!row.isValid) return null;

    return (
      <Checkbox
        checked={selectedRowIds.has(row.id)}
        onChange={(e) => {
          e.stopPropagation();
          onRowSelectionChange(row.id, e.target.checked);
        }}
        size="small"
        disableRipple
      />
    );
  };
}

function createSelectHeaderRenderer(displayRows: ValidatedGDORow[], selectedRowIds: Set<number>, onSelectAllValid: (selected: boolean) => void) {
  return ({ column }: { column: Column<ValidatedGDORow> }) => {
    const allValidRows = displayRows.filter((r) => r.isValid);
    const allSelected = allValidRows.length > 0 && allValidRows.every((r) => selectedRowIds.has(r.id));

    return (
      <Checkbox
        checked={allSelected}
        onChange={(e) => {
          e.stopPropagation();
          onSelectAllValid(e.target.checked);
        }}
        size="small"
        disableRipple
        aria-label="Select all valid rows"
      />
    );
  };
}

function createDataCellFormatter(key: ColumnKey) {
  return ({ row }: { row: ValidatedGDORow }) => {
    const value = String(row[key] || '');
    const error = row.cellErrors?.[key];
    const isErrorRow = !row.isValid;
    const bgColor = error ? ERROR_CELL_BG : (isErrorRow ? ERROR_ROW_BG : 'transparent');

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.5,
          width: '100%',
          height: '100%',
          backgroundColor: bgColor,
          borderRadius: 1,
          position: 'relative',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
          {value || '\u2014'}
        </span>
        {error && (
          <Tooltip title={error} placement="top" enterDelay={300}>
            <WarningIcon
              sx={{
                position: 'absolute',
                right: 6,
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: 14,
                color: '#D32F2F',
                cursor: 'help',
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  };
}

const GDOValidationPreviewTableWrapper: React.FC<GDOValidationPreviewTableProps> = (props) => {
  const displayRows = useMemo(() => {
    const filtered = props.showValidOnly ? props.rows.filter((r) => r.isValid) : props.rows;
    return [...filtered].sort((a, b) => a.gdoName.localeCompare(b.gdoName));
  }, [props.rows, props.showValidOnly]);

  const handleRowSelection = useCallback((rowId: number, selected: boolean) => {
    props.onRowSelectionChange(rowId, selected);
  }, [props.onRowSelectionChange]);

  const handleSelectAllValid = useCallback((selected: boolean) => {
    props.onSelectAllValid(selected);
  }, [props.onSelectAllValid]);

  return <GDOValidationPreviewTable {...props} onRowSelectionChange={handleRowSelection} onSelectAllValid={handleSelectAllValid} />;
};

export default GDOValidationPreviewTableWrapper;